import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';
import * as yup from 'yup';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import HotelSearchSelect, { Option } from 'components/HotelSearchSelect';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';

import { AssignHotelToPackageDialog_package$key } from '__generated__/AssignHotelToPackageDialog_package.graphql';
import { AssignHotelToPackageDialog_data$key } from '__generated__/AssignHotelToPackageDialog_data.graphql';
import { AssignHotelToPackageDialogMutation } from '__generated__/AssignHotelToPackageDialogMutation.graphql';

type FormFields = {
  hotel: Option | null;
};

type Props = {
  package: AssignHotelToPackageDialog_package$key;
  data: AssignHotelToPackageDialog_data$key;
};

const initial = {
  hotel: null,
};

const schema = yup.object({
  hotel: yup.object().nullable().required('Prašome pasirinkti viešbutį'),
});

const AssignHotelToPackageDialog: FC<Props> = props => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const { id } = useFragment(
    graphql`
      fragment AssignHotelToPackageDialog_package on Package {
        id
      }
    `,
    props.package
  );

  const data = useFragment(
    graphql`
      fragment AssignHotelToPackageDialog_data on Query {
        ...HotelSearchSelect_data
      }
    `,
    props.data
  );

  const [assign, assigning] =
    useMutation<AssignHotelToPackageDialogMutation>(graphql`
      mutation AssignHotelToPackageDialogMutation(
        $id: ID!
        $input: AssignHotelInput!
      ) {
        assignHotelToPackage(id: $id, input: $input) {
          ...PackageEditAccommodation_package
        }
      }
    `);

  const handleSubmit = useCallback(
    ({ hotel }: FormFields) => {
      const { name, ...input } = hotel ?? {};
      assign({
        variables: { id, input },

        ...errorHandler({
          onCompleted: close,
        }),
      });
    },
    [assign, close, errorHandler, id]
  );

  return (
    <Formik<FormFields>
      initialValues={initial}
      validationSchema={schema}
      onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Pasiūlymo viešbutis</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Priskirkite kelionei viešbutį kurį svetainės vartotojai galės vėliau
            užsakyti.
          </DialogContentText>

          <HotelSearchSelect
            autoFocus
            data={data}
            label="Viešbutis"
            name="hotel"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={assigning}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Saugoti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default AssignHotelToPackageDialog;
